import React from 'react';
import { motion } from 'framer-motion';

export default function SkillsSection({ skillsSection, techStack }) {

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100 },
    },
  };

  return (
    <section id="skills" className="py-20 px-4 bg-gray-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {skillsSection.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {skillsSection.subTitle}
          </p>
        </motion.div>

        {/* Skills description */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6 mb-12"
        >
          {skillsSection.skills.map((skill, i) => (
            <motion.div
              key={i}
              variants={itemVariant}
              className="group p-6 rounded-xl glass hover:shadow-xl"
            >
              <p
                className="text-gray-700 dark:text-gray-300 leading-relaxed"
              >
                {skill}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Tech Stack with icons */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 className="text-2xl font-bold mb-8 text-center">Technology Stack</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {skillsSection.softwareSkills.map((skill, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="flex flex-col items-center p-4 rounded-lg bg-white dark:bg-slate-800 hover:shadow-lg"
              >
                <i className={`${skill.fontAwesomeClassname} text-3xl mb-2 text-primary-500`} />
                <p className="text-xs font-medium text-center">{skill.skillName}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tech Stack Proficiency */}
        {techStack.viewSkillBars && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <h3 className="text-2xl font-bold mb-8 text-center">Expertise Level</h3>
            <div className="space-y-8 max-w-3xl mx-auto">
              {techStack.experience.map((exp, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {exp.Stack}
                    </span>
                    <span className="text-primary-500 font-bold">{exp.progressPercentage}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: exp.progressPercentage }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                      className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
